import { ELEMENT_TYPES } from "../constants/boqElementTypes";

/**
 * GAEB-compliant BoQ Position Number (OZ / Ordnungszahl) generator.
 *
 * Format rules:
 *   Group              → GG             e.g. "01", "02"
 *   Subgroup           → GG.SS          e.g. "01.01"
 *   Position in group  → GG._.PPPP      e.g. "01._.0010"  (step 10)
 *   Position in sub    → GG.SS.PPPP     e.g. "01.01.0010" (step 10)
 *   Note / AddlText    → null
 *   ExecDescription    → EEEE           e.g. "0001" (flat tree-wide counter)
 */

// Local alias for readability (single source of truth is ELEMENT_TYPES).
const T = ELEMENT_TYPES;

// ─── Schema parameter helpers ───────────────────────────────────────────────

/**
 * Derive low-level generation parameters from an AVA itemNumberSchema object.
 * Falls back to GAEB defaults (GG.SS.PPPP / step 10) when schema is null.
 *
 * @param {Object|null} schema  – projectInformation.itemNumberSchema from AVA
 * @returns {{ groupLen, subLen, posLen, posStep, groupStep, subStep, sep, skipFiller }}
 */
function _resolveSchemaParams(schema) {
	if (!schema?.tiers) {
		return {
			groupLen: 2,
			subLen: 2,
			posLen: 4,
			posStep: 10,
			groupStep: 1,
			subStep: 1,
			sep: ".",
			skipFiller: "_",
		};
	}
	const groupTiers = schema.tiers.filter((t) => t.tierType === "Group");
	const posTier = schema.tiers.find((t) => t.tierType === "Position");
	const tier0 = groupTiers[0] ?? { length: 2, increment: 1 };
	const tier1 = groupTiers[1] ?? { length: 2, increment: 1 };
	return {
		groupLen: tier0.length,
		subLen: tier1.length,
		posLen: posTier?.length ?? 4,
		posStep: posTier?.increment ?? 10,
		groupStep: tier0.increment ?? 1,
		subStep: tier1.increment ?? 1,
		sep: schema.separator ?? ".",
		skipFiller: schema.skippedTiersFiller ?? "_",
	};
}

/**
 * Build OZ validation regex patterns from a schema.
 * @param {Object|null} schema
 * @returns {Object}  – keyed by ELEMENT_TYPES value
 */
export function buildOzPatterns(schema) {
	const { groupLen, subLen, posLen, sep, skipFiller } =
		_resolveSchemaParams(schema);
	const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const escSep = esc(sep);
	const skipSub = esc(skipFiller.repeat(subLen));
	return {
		[T.GROUP]: new RegExp(`^\\d{${groupLen}}\\.?$`),
		[T.SUBGROUP]: new RegExp(
			`^\\d{${groupLen}}${escSep}\\d{${subLen}}\\.?$`,
		),
		[T.POSITION]: new RegExp(
			`^\\d{${groupLen}}${escSep}(\\d{${subLen}}|${skipSub})${escSep}\\d{${posLen}}\\.?$`,
		),
		[T.EXECUTION_DESCRIPTION]: /^\d{4}\.?$/,
	};
}

// ─── OZ validation patterns ───────────────────────────────────────────────────
// Static default patterns (GAEB GG.SS.PPPP / step 10 / separator ".").
// Use buildOzPatterns(schema) when a custom schema is available.
export const OZ_PATTERNS = buildOzPatterns(null);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Replace the leading `oldPrefix` with `newPrefix` in a node's itemNumber
 * and recurse into children.
 *
 * Both prefixes are normalised (trailing dot stripped) before matching so that
 * AVA-formatted OZs like "04." and user-typed "04" are treated identically.
 */
function _replacePrefix(node, oldPrefix, newPrefix) {
	// Normalise: strip trailing dots so "04." and "04" compare equal.
	const oldClean = _stripTrailingDot(oldPrefix);
	const newClean = _stripTrailingDot(newPrefix);

	const updated = { ...node };
	if (typeof updated.itemNumber === "string") {
		const clean = _stripTrailingDot(updated.itemNumber);
		if (clean === oldClean) {
			// Exact match - the node itself is being renamed (shouldn't normally
			// happen via _replacePrefix, but guard anyway).
			updated.itemNumber = newClean;
		} else if (clean.startsWith(oldClean + ".")) {
			// Child OZ: replace leading prefix, preserve the rest (keep original
			// trailing dot if present so AVA-formatted values stay consistent).
			const rest = updated.itemNumber.slice(
				updated.itemNumber.indexOf(oldClean) + oldClean.length,
			);
			updated.itemNumber = newClean + rest;
		}
	}
	if (Array.isArray(updated.children)) {
		updated.children = updated.children.map((child) =>
			_replacePrefix(child, oldClean, newClean),
		);
	}
	return updated;
}

/**
 * Strip any trailing dots from an OZ string so it can be used as a prefix
 * when building child item numbers.  AVA Cloud appends a trailing dot to every
 * tier (Index tier indicator) - we must not carry that into constructed strings.
 */
function _stripTrailingDot(oz) {
	return oz ? String(oz).replace(/\.+$/, "") : "";
}

/**
 * Recursive worker.
 *
 * @param {Array}   nodes
 * @param {string}  parentType  – T.GROUP | T.SUBGROUP | null
 * @param {string}  parentOZ    – clean (no trailing dot) OZ string of the parent
 * @param {object}  state       – shared mutable: { execDescIdx }
 * @param {boolean} force       – when true, ignore existing itemNumber values
 *                                and regenerate everything from scratch
 *
 * When `force` is false (default):
 *   • Nodes that already have a non-empty itemNumber keep it unchanged.
 *   • New nodes (itemNumber === "" or null) receive a number derived from:
 *       – the maximum index already in use among siblings of the same type
 *       – the parent's actual (preserved) OZ prefix
 *   This ensures a manually-renamed group ("04") is never silently reset
 *   back to "01" just because a sibling node was added.
 */
/**
 * @param {Array}   nodes
 * @param {string}  parentType  – T.GROUP | T.SUBGROUP | null
 * @param {string}  parentOZ    – clean (no trailing dot) OZ string of the parent
 * @param {object}  state       – shared mutable: { execDescIdx }
 * @param {boolean} force       – when true, ignore existing itemNumber values
 * @param {object}  sp          – resolved schema params from _resolveSchemaParams()
 */
function _processLevel(nodes, parentType, parentOZ, state, force, sp) {
	const padGroup = (n) => String(n).padStart(sp.groupLen, "0");
	const padSub = (n) => String(n).padStart(sp.subLen, "0");
	const padPos = (n) => String(n).padStart(sp.posLen, "0");
	const skipSubgroup = sp.skipFiller.repeat(sp.subLen);
	const skipGroup = sp.skipFiller.repeat(sp.groupLen);
	const { sep, posStep } = sp;

	// ── Pre-scan: find the highest indices already occupied at this level ──────
	let maxContainerIdx = 0; // used for GROUP / SUBGROUP
	let maxPositionIdx = 0; // used for POSITION (in steps of posStep)

	if (!force) {
		for (const node of nodes) {
			const oz = _stripTrailingDot(node.itemNumber);
			if (!oz) continue;
			const parts = oz.split(sep).filter(Boolean);
			if (node.type === T.GROUP) {
				const n = parseInt(parts[0], 10);
				if (!isNaN(n)) maxContainerIdx = Math.max(maxContainerIdx, n);
			} else if (node.type === T.SUBGROUP) {
				const n = parseInt(parts[parts.length - 1], 10);
				if (!isNaN(n)) maxContainerIdx = Math.max(maxContainerIdx, n);
			} else if (node.type === T.POSITION) {
				const posStr = parts[parts.length - 1];
				const n = parseInt(posStr, 10);
				if (!isNaN(n))
					maxPositionIdx = Math.max(
						maxPositionIdx,
						Math.floor(n / posStep),
					);
			} else if (node.type === T.EXECUTION_DESCRIPTION) {
				const n = parseInt(oz, 10);
				if (!isNaN(n))
					state.execDescIdx = Math.max(state.execDescIdx, n);
			}
		}
	}

	return nodes.map((node) => {
		const updated = { ...node };
		const type = node.type;
		const existingOZ = node.itemNumber;
		const needsOZ = force || !existingOZ || existingOZ === "";

		if (type === T.GROUP) {
			let oz;
			if (!needsOZ) {
				oz = existingOZ;
			} else {
				maxContainerIdx += sp.groupStep;
				oz = padGroup(maxContainerIdx);
				updated.itemNumber = oz;
			}
			const cleanOZ = _stripTrailingDot(oz);
			if (Array.isArray(node.children)) {
				updated.children = _processLevel(
					node.children,
					T.GROUP,
					cleanOZ,
					state,
					force,
					sp,
				);
			}
		} else if (type === T.SUBGROUP) {
			let oz;
			if (!needsOZ) {
				oz = existingOZ;
			} else {
				maxContainerIdx += sp.subStep;
				oz = `${parentOZ}${sep}${padSub(maxContainerIdx)}`;
				updated.itemNumber = oz;
			}
			const cleanOZ = _stripTrailingDot(oz);
			if (Array.isArray(node.children)) {
				updated.children = _processLevel(
					node.children,
					T.SUBGROUP,
					cleanOZ,
					state,
					force,
					sp,
				);
			}
		} else if (type === T.POSITION) {
			if (!needsOZ) {
				// preserve - do nothing
			} else {
				maxPositionIdx += 1;
				const posStr = padPos(maxPositionIdx * posStep);
				if (parentType === T.GROUP) {
					// Direct child of a group: fill skipped subgroup tier with filler × subLen
					updated.itemNumber = `${parentOZ}${sep}${skipSubgroup}${sep}${posStr}`;
				} else if (parentType === T.SUBGROUP) {
					updated.itemNumber = `${parentOZ}${sep}${posStr}`;
				} else {
					// Top-level position (unusual but handle gracefully)
					updated.itemNumber = `${skipGroup}${sep}${skipSubgroup}${sep}${posStr}`;
				}
			}
		} else if (type === T.NOTE || type === T.ADDITIONAL_TEXT) {
			updated.itemNumber = null;
		} else if (type === T.EXECUTION_DESCRIPTION) {
			if (!needsOZ) {
				// preserve - state.execDescIdx was already bumped in pre-scan
			} else {
				state.execDescIdx += 1;
				updated.itemNumber = String(state.execDescIdx).padStart(4, "0");
			}
		}

		return updated;
	});
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Assign OZ numbers to any nodes in the tree that are missing one.
 * Existing, non-empty itemNumber values are preserved so that manually-set
 * OZ edits (e.g. renaming group "01" → "04") survive structural operations
 * such as adding or removing a sibling row.
 *
 * Pass `{ force: true }` to bypass preservation and regenerate every OZ from
 * scratch (used by the "Reset OZ" button).
 *
 * @param {Array}   tree
 * @param {object}  [options]
 * @param {boolean} [options.force=false]
 * @returns {Array}
 */
export function generateItemNumbers(
	tree,
	{ force = false, schema = null } = {},
) {
	if (!Array.isArray(tree)) return tree;
	const state = { execDescIdx: 0 };
	const sp = _resolveSchemaParams(schema);
	return _processLevel(tree, null, null, state, force, sp);
}

/**
 * Validate OZ numbers in the tree.
 * @param {Array} tree
 * @returns {Array<{ rowId: string, message: string, errorType: string }>}
 */
export function validateItemNumbers(tree, schema = null) {
	const patterns = buildOzPatterns(schema);
	const errors = [];
	const seen = new Map(); // itemNumber → first rowId

	function walk(nodes) {
		for (const node of nodes) {
			const { id, type, itemNumber } = node;
			const pattern = patterns[type];

			if (pattern) {
				// Must have an OZ
				if (itemNumber == null || itemNumber === "") {
					errors.push({
						rowId: id,
						message: "Missing OZ number",
						errorType: "missing",
					});
				} else if (!pattern.test(itemNumber)) {
					errors.push({
						rowId: id,
						message: `Invalid OZ format: "${itemNumber}"`,
						errorType: "format",
					});
				} else {
					// Duplicate check
					if (seen.has(itemNumber)) {
						errors.push({
							rowId: id,
							message: `Duplicate OZ: "${itemNumber}"`,
							errorType: "duplicate",
						});
					} else {
						seen.set(itemNumber, id);
					}
				}
			} else if (type === T.NOTE || type === T.ADDITIONAL_TEXT) {
				// These should NOT have an OZ
				if (itemNumber != null && itemNumber !== "") {
					errors.push({
						rowId: id,
						message: `Notes must not have an OZ (found "${itemNumber}")`,
						errorType: "unexpected",
					});
				}
			}

			if (Array.isArray(node.children)) {
				walk(node.children);
			}
		}
	}

	walk(tree);
	return errors;
}

/**
 * After the user manually changes a node's OZ, resequence all same-type siblings
 * that appear AFTER it (by array order) so they follow sequentially from the new
 * number.  Children of resequenced siblings have their prefix updated too.
 *
 * Examples (groupStep = 1):
 *   Groups [01, 02, 03, 04], user changes 01 → 08  ⟹  [08, 09, 10, 11]
 *   Groups [01, 02, 03], user changes 02 → 05       ⟹  [01, 05, 06]
 *
 * @param {Array}        tree    – current editor tree (already has newOZ applied)
 * @param {string}       nodeId  – id of the node whose OZ was just changed
 * @param {string|null}  newOZ   – the new itemNumber value
 * @param {object|null}  [schema]
 * @returns {Array}
 */
export function resequenceSiblingsAfter(tree, nodeId, newOZ, schema = null) {
	if (!newOZ) return tree;
	const sp = _resolveSchemaParams(schema);

	function processLevel(nodes) {
		// Find the changed node at this level
		const idx = nodes.findIndex((n) => n.id === nodeId);
		if (idx === -1) {
			// Not at this level - recurse into children
			let changed = false;
			const next = nodes.map((n) => {
				if (!Array.isArray(n.children)) return n;
				const newChildren = processLevel(n.children);
				if (newChildren === n.children) return n;
				changed = true;
				return { ...n, children: newChildren };
			});
			return changed ? next : nodes;
		}

		const nodeType = nodes[idx].type;
		// Only resequence GROUP / SUBGROUP / POSITION
		if (
			nodeType !== T.GROUP &&
			nodeType !== T.SUBGROUP &&
			nodeType !== T.POSITION
		)
			return nodes;

		const cleanNew = _stripTrailingDot(newOZ);
		const newParts = cleanNew.split(sp.sep).filter(Boolean);
		const newLocalNum = parseInt(newParts[newParts.length - 1], 10);
		if (isNaN(newLocalNum)) return nodes;

		const step =
			nodeType === T.POSITION
				? sp.posStep
				: nodeType === T.GROUP
					? sp.groupStep
					: sp.subStep;

		const parentPrefix = newParts.slice(0, -1).join(sp.sep);

		const padLen =
			nodeType === T.POSITION
				? sp.posLen
				: nodeType === T.GROUP
					? sp.groupLen
					: sp.subLen;
		const pad = (n) => String(n).padStart(padLen, "0");

		let counter = newLocalNum;
		return nodes.map((node, i) => {
			if (i <= idx) return node;
			if (node.type !== nodeType) return node; // skip different types

			counter += step;
			const localStr = pad(counter);
			const oldOZ = node.itemNumber;
			const updatedOZ = parentPrefix
				? `${parentPrefix}${sp.sep}${localStr}`
				: localStr;

			let updated = { ...node, itemNumber: updatedOZ };
			// Cascade the OZ prefix change to descendants
			if (Array.isArray(node.children) && oldOZ != null) {
				updated.children = node.children.map((child) =>
					_replacePrefix(child, oldOZ, updatedOZ),
				);
			}
			return updated;
		});
	}

	return processLevel(tree);
}

/**
 * When the user manually changes a group's or subgroup's OZ, cascade the
 * prefix change to all descendants.
 *
 * @param {Array}  tree     – current editor tree
 * @param {string} nodeId   – id of the node whose itemNumber changed
 * @param {string|null} newOZ – the new itemNumber value
 * @returns {Array} – new tree with updated descendants
 */
export function cascadeItemNumberChange(tree, nodeId, newOZ) {
	let oldOZ = null;

	// First pass: find the old OZ of this node
	function findOld(nodes) {
		for (const node of nodes) {
			if (node.id === nodeId) {
				oldOZ = node.itemNumber;
				return true;
			}
			if (Array.isArray(node.children) && findOld(node.children))
				return true;
		}
		return false;
	}
	findOld(tree);

	// Second pass: update the node and cascade to descendants
	function applyChange(nodes) {
		return nodes.map((node) => {
			if (node.id === nodeId) {
				const updated = { ...node, itemNumber: newOZ };
				if (
					oldOZ != null &&
					newOZ != null &&
					Array.isArray(updated.children)
				) {
					updated.children = updated.children.map((child) =>
						_replacePrefix(child, oldOZ, newOZ),
					);
				}
				return updated;
			}
			if (Array.isArray(node.children)) {
				return { ...node, children: applyChange(node.children) };
			}
			return node;
		});
	}

	return applyChange(tree);
}
